import { AxiosError } from 'axios';
import {
  FC,
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { API } from '../api';
import { useAppDispatch } from '../state';
import { useAuthSelector } from '../state/auth/selectors';
import { logoutAction } from '../state/auth/slice';
import { useMeSelector } from '../state/me/selectors';
import { meActions } from '../state/me/slice';
import { Paths } from '../types/common';

const unAuthPaths: string[] = [
  Paths.LOGIN,
  Paths.LOGIN_STUDENT,
  Paths.FORGOT_PASSWORD,
  Paths.REGISTRATION,
  Paths.FORGOT_COMPLETE,
];

export const AuthService: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { authed, data: authData } = useAuthSelector();
  const { meData, meLoaded, meError, mePending } = useMeSelector();
  const navigate = useNavigate();
  const pendingRef = useRef(false);
  const isStudentSession =
    meData?.role === 0 || !!authData?.account.studentMode;

  useEffect(() => {
    if (
      unAuthPaths.includes(location.pathname) &&
      authed &&
      location.pathname !== Paths.FORGOT_PASSWORD &&
      (location.pathname !== Paths.LOGIN_STUDENT || isStudentSession)
    ) {
      navigate(isStudentSession ? Paths.PLAYLISTS : Paths.BASE);
    } else if (
      !authed &&
      !unAuthPaths.includes(location.pathname) &&
      !unAuthPaths.includes(
        location.pathname.split('/').map((el) => `/${el}`)[1]
      )
    ) {
      navigate(Paths.LOGIN);
    }
  }, [authed, isStudentSession, location, navigate]);

  useEffect(() => {
    if (!authed || !meLoaded || !isStudentSession) return;

    const studentPathAllowed =
      location.pathname === Paths.ACCOUNT ||
      location.pathname === Paths.PLAYLISTS ||
      location.pathname.startsWith(`${Paths.PLAYLISTS}/`) ||
      location.pathname === Paths.PLAYCOURSE ||
      location.pathname.startsWith(`${Paths.PLAYCOURSE}/`) ||
      (location.pathname === Paths.MODULE1_VIEW &&
        new URLSearchParams(location.search).get('embedded') === '1');

    if (!studentPathAllowed) {
      navigate(Paths.PLAYLISTS, { replace: true });
    }
  }, [
    authed,
    isStudentSession,
    location.pathname,
    location.search,
    meLoaded,
    navigate,
  ]);

  const handleLoadMe = useCallback(async () => {
    try {
      pendingRef.current = true;
      dispatch(meActions.startMe());

      const response = await API.me.getMyAccount();

      dispatch(meActions.finishMe(response.data));

      pendingRef.current = false;
    } catch (e: unknown) {
      pendingRef.current = false;
      dispatch(meActions.errorMe());
      // @ts-ignore
      if ('response' in (e as AxiosError) && e.response.status === 403) {
        dispatch(logoutAction());
      }

      // @ts-ignore
      if (e && e?.response && e?.response?.status === 401) {
        dispatch(logoutAction());
      }
    }
  }, []);

  useEffect(() => {
    if (authed && !pendingRef.current && !meLoaded && !mePending && !meError) {
      void handleLoadMe();
    }
  }, [authed, meLoaded, mePending, meError]);

  return <Fragment>{children}</Fragment>;
};
